/**
 * Global Loyalty Pool dashboard — agency owner only.
 *
 * Four tabs:
 *   • Overview        running balances + recent ledger
 *   • Members         top members by points balance
 *   • Ledger          filterable append-only log
 *   • Reconciliation  per-tenant monthly net position
 */
import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wallet,
  Users,
  ListChecks,
  Calculator,
  TrendingUp,
  TrendingDown,
  Settings as SettingsIcon,
  RefreshCw,
  Cog,
} from "lucide-react";
import LoyaltyConnectSetupTab from "@/components/LoyaltyConnectSetupTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const aud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
    Number(n || 0)
  );

const fmtInt = (n) =>
  new Intl.NumberFormat("en-AU").format(Math.round(Number(n || 0)));

function token() {
  return localStorage.getItem("token") || "";
}
const authHeaders = () => ({
  Authorization: `Bearer ${token()}`,
  "Content-Type": "application/json",
});

function KpiCard({ icon: Icon, label, value, hint, accent = "text-stone-700", testid }) {
  return (
    <Card data-testid={testid} className="border-stone-200/70">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-xs uppercase tracking-wide text-stone-500">
          {label}
        </CardTitle>
        <Icon className={`h-4 w-4 ${accent}`} strokeWidth={1.75} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold text-stone-900">{value}</div>
        {hint ? <div className="text-xs text-stone-500 mt-1">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

function LedgerKindBadge({ kind }) {
  const map = {
    earn: { tone: "bg-emerald-50 text-emerald-700", label: "Earn" },
    welcome_bonus: { tone: "bg-amber-50 text-amber-700", label: "Welcome" },
    redeem: { tone: "bg-rose-50 text-rose-700", label: "Redeem" },
    pool_contribution: { tone: "bg-sky-50 text-sky-700", label: "Pool +" },
    admin_fee: { tone: "bg-violet-50 text-violet-700", label: "Admin fee" },
    settlement: { tone: "bg-stone-100 text-stone-700", label: "Settlement" },
  };
  const m = map[kind] || { tone: "bg-stone-100 text-stone-700", label: kind };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.tone}`}>
      {m.label}
    </span>
  );
}

export default function AdminLoyaltyPoolPage() {
  const [stats, setStats] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [members, setMembers] = useState([]);
  const [recon, setRecon] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, m, r] = await Promise.all([
        fetch(`${API}/admin/global-loyalty/stats`, { headers: authHeaders() }).then((r) => r.json()),
        fetch(`${API}/admin/global-loyalty/members?limit=50&order=balance`, {
          headers: authHeaders(),
        }).then((r) => r.json()),
        fetch(`${API}/admin/global-loyalty/reconciliation`, { headers: authHeaders() }).then(
          (r) => r.json()
        ),
      ]);
      setStats(s);
      setSettings(s?.settings || null);
      setMembers(m?.rows || []);
      setRecon(r);
    } catch (e) {
      toast.error("Failed to load pool stats");
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async () => {
    const params = new URLSearchParams();
    if (filterKind) params.set("kind", filterKind);
    if (filterMonth) params.set("month", filterMonth);
    params.set("limit", "200");
    try {
      const j = await fetch(`${API}/admin/global-loyalty/ledger?${params}`, {
        headers: authHeaders(),
      }).then((r) => r.json());
      setLedger(j?.rows || []);
    } catch {
      toast.error("Failed to load ledger");
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);
  useEffect(() => {
    fetchLedger();
  }, [filterKind, filterMonth]);

  const state = stats?.state || {};
  const totals = recon?.totals || {};

  const kindOptions = useMemo(
    () => [
      ["", "All kinds"],
      ["earn", "Earn"],
      ["redeem", "Redeem"],
      ["pool_contribution", "Pool contribution"],
      ["admin_fee", "Admin fee"],
      ["welcome_bonus", "Welcome bonus"],
      ["settlement", "Settlement"],
    ],
    []
  );

  return (
    <div className="space-y-6 p-6" data-testid="admin-loyalty-pool">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Global Loyalty Pool</h1>
          <p className="text-sm text-stone-500 mt-1 max-w-2xl">
            Cross-store points wallet funded by a {((settings?.pool_fee_pct || 0) * 100).toFixed(2)}%
            slice of every paid order. Members earn at any participating store and redeem at any
            other. Reconciliation runs monthly.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          data-testid="loyalty-pool-refresh"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <KpiCard
          testid="kpi-pool-balance"
          icon={Wallet}
          label="Pool balance"
          value={aud(state.pool_balance_aud)}
          hint={`Lifetime in: ${aud(state.lifetime_contributions_aud)} · out: ${aud(
            state.lifetime_redemptions_aud
          )}`}
          accent={Number(state.pool_balance_aud) >= 0 ? "text-emerald-600" : "text-rose-600"}
        />
        <KpiCard
          testid="kpi-admin-fees"
          icon={TrendingUp}
          label="Platform admin fees"
          value={aud(state.admin_fee_balance_aud)}
          hint={`Lifetime: ${aud(state.lifetime_admin_fees_aud)}`}
          accent="text-violet-600"
        />
        <KpiCard
          testid="kpi-members"
          icon={Users}
          label="Active members"
          value={fmtInt(state.total_members)}
          hint={`${fmtInt(state.points_outstanding)} points outstanding`}
          accent="text-sky-600"
        />
        <KpiCard
          testid="kpi-fees"
          icon={SettingsIcon}
          label="Fee split"
          value={`${((settings?.pool_fee_pct || 0) * 100).toFixed(2)}% + ${(
            (settings?.admin_fee_pct || 0) * 100
          ).toFixed(2)}%`}
          hint={`${settings?.points_redemption_rate || 100} pts = $1 · min ${
            settings?.min_points_to_redeem
          } pts`}
        />
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            Members
          </TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            Ledger
          </TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-recon">
            Reconciliation
          </TabsTrigger>
          <TabsTrigger value="setup" data-testid="tab-setup">
            <Cog className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.75} />
            Setup
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent activity</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Type</th>
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Business</th>
                    <th className="px-4 py-2 text-right">Points</th>
                    <th className="px-4 py-2 text-right">AUD</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.recent_ledger || []).map((row) => (
                    <tr key={row.id} className="border-b border-stone-50">
                      <td className="px-4 py-2 text-stone-500 whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString("en-AU", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-4 py-2">
                        <LedgerKindBadge kind={row.kind} />
                      </td>
                      <td className="px-4 py-2 text-stone-700">{row.email || "—"}</td>
                      <td className="px-4 py-2 text-stone-700">{row.business_id || "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.points ? fmtInt(row.points) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.amount_aud ? aud(row.amount_aud) : "—"}
                      </td>
                    </tr>
                  ))}
                  {(stats?.recent_ledger || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-stone-400">
                        No pool activity yet — first paid order will populate this.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MEMBERS */}
        <TabsContent value="members" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2 text-right">Earned (life)</th>
                    <th className="px-4 py-2 text-right">Redeemed (life)</th>
                    <th className="px-4 py-2 text-right">Stores</th>
                    <th className="px-4 py-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} className="border-b border-stone-50" data-testid={`member-row-${m.email}`}>
                      <td className="px-4 py-2 text-stone-700">{m.email}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">
                        {fmtInt(m.points_balance)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtInt(m.points_earned_lifetime)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtInt(m.points_redeemed_lifetime || 0)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Badge variant="secondary">
                          {(m.businesses_visited || []).length}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-stone-500">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString("en-AU") : "—"}
                      </td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-stone-400">
                        No members yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEDGER */}
        <TabsContent value="ledger" className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Kind</Label>
              <select
                data-testid="ledger-filter-kind"
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value)}
                className="block mt-1 border border-stone-300 rounded px-3 py-1.5 text-sm bg-white"
              >
                {kindOptions.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Month (YYYY-MM)</Label>
              <Input
                data-testid="ledger-filter-month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                placeholder="2026-05"
                className="mt-1 w-32"
              />
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="px-4 py-2">When</th>
                    <th className="px-4 py-2">Kind</th>
                    <th className="px-4 py-2">Member</th>
                    <th className="px-4 py-2">Business</th>
                    <th className="px-4 py-2 text-right">Points</th>
                    <th className="px-4 py-2 text-right">AUD</th>
                    <th className="px-4 py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((row) => (
                    <tr key={row.id} className="border-b border-stone-50">
                      <td className="px-4 py-2 text-stone-500 whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString("en-AU", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </td>
                      <td className="px-4 py-2">
                        <LedgerKindBadge kind={row.kind} />
                      </td>
                      <td className="px-4 py-2 text-stone-700">{row.email || "—"}</td>
                      <td className="px-4 py-2 text-stone-700">{row.business_id || "—"}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.points ? fmtInt(row.points) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {row.amount_aud ? aud(row.amount_aud) : "—"}
                      </td>
                      <td className="px-4 py-2 text-stone-500 text-xs">{row.note}</td>
                    </tr>
                  ))}
                  {ledger.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-stone-400">
                        Nothing matches the filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RECONCILIATION */}
        <TabsContent value="recon" className="space-y-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
            <KpiCard
              icon={TrendingUp}
              label="Month contributions"
              value={aud(totals.contributed_aud)}
              hint={`Month: ${recon?.month_key || "—"}`}
              accent="text-sky-600"
            />
            <KpiCard
              icon={TrendingDown}
              label="Month redemptions"
              value={aud(totals.redeemed_aud)}
              hint="Pool obligations created"
              accent="text-rose-600"
            />
            <KpiCard
              icon={Calculator}
              label="Net pool movement"
              value={aud(totals.net_pool_movement_aud)}
              hint={
                Number(totals.net_pool_movement_aud) >= 0
                  ? "Pool grew this month"
                  : "Pool drained this month"
              }
              accent={
                Number(totals.net_pool_movement_aud) >= 0 ? "text-emerald-600" : "text-rose-600"
              }
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-tenant net position</CardTitle>
              <p className="text-xs text-stone-500">
                Positive "net owed by pool" = the pool owes the tenant cash (they paid out more
                redemptions than they contributed this month). Settlement transfers will be wired
                in Phase B via Stripe Connect.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-left text-stone-500 border-b border-stone-100">
                  <tr>
                    <th className="px-4 py-2">Business</th>
                    <th className="px-4 py-2 text-right">Contributed</th>
                    <th className="px-4 py-2 text-right">Redeemed</th>
                    <th className="px-4 py-2 text-right">Net owed by pool</th>
                    <th className="px-4 py-2 text-right"># in</th>
                    <th className="px-4 py-2 text-right"># out</th>
                  </tr>
                </thead>
                <tbody>
                  {(recon?.tenants || []).map((row) => (
                    <tr
                      key={row.business_id}
                      className="border-b border-stone-50"
                      data-testid={`recon-row-${row.business_id}`}
                    >
                      <td className="px-4 py-2 text-stone-700">{row.business_name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {aud(row.contributed_aud)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {aud(row.redeemed_at_aud)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right tabular-nums font-medium ${
                          row.net_owed_by_pool_aud > 0
                            ? "text-emerald-700"
                            : row.net_owed_by_pool_aud < 0
                            ? "text-rose-700"
                            : "text-stone-500"
                        }`}
                      >
                        {aud(row.net_owed_by_pool_aud)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtInt(row.contributions_count)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {fmtInt(row.redemptions_count)}
                      </td>
                    </tr>
                  ))}
                  {(recon?.tenants || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-stone-400">
                        No reconciliation data for this month yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETUP — Stripe Connect onboarding wizard + AI Concierge */}
        <TabsContent value="setup" className="space-y-4">
          <LoyaltyConnectSetupTab poolState={state} />
        </TabsContent>
      </Tabs>
    </div>
  );
}